import {
    Component,
    Input,
    Output,
    OnInit,
    OnChanges,
    SimpleChange,
    EventEmitter
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Observable, Subscription, Subject } from 'rxjs/';

import { NotificationService } from '../../../core/services/notification.service';
import { UtilService } from '../../../core/services/util.service';

import { QuestionControlService } from './question-control.service';
import { FormService } from './form.service';

import { QuestionBase } from './models/question-base.model';
import { TextboxQuestion } from './models/question-textbox.model';
import { NumberboxQuestion } from './models/question-numberbox.model';
import { TextareaQuestion } from './models/question-textarea.model';
import { TextDateQuestion } from './models/question-text-date.model';
import { RadioQuestion } from './models/question-radio.model';
import { TextQuestion } from './models/question-text.model';
import { HiddenQuestion } from './models/question-hidden.model';
import { DropdownQuestion } from './models/question-dropdown.model';
import { MultiDropdownQuestion } from './models/question-multi-dropdown.model';
import { MultiEditQuestion } from './models/question-multi-edit.model';
import { ComboboxQuestion } from './models/question-combobox.model';
import { ComboboxSingleSelectQuestion } from './models/question-combobox-single-select.model';
import { DatepickerQuestion } from './models/question-datepicker.model';
import { DatetimepickerQuestion } from './models/question-datetimepicker.model';
import { DynamicDropdownQuestion } from './models/question-dynamic-dropdown.model';
import { AutocompleteQuestion } from './models/question-autocomplete.model';
import { CheckboxQuestion } from './models/question-checkbox.model';
import { ListQuestion } from './models/question-list.model';
import { HtmlEditorQuestion } from 'app/shared/components/form/models/question-html-editor.model';
import { Router } from '@angular/router';
import { ImgUploadQuestion } from './models/question-img-upload.model';

@Component({
    selector: 'itpx-form',
    templateUrl: 'form.component.html',
    styleUrls: ['./form.component.css'],
    providers: [QuestionControlService, FormService]
})
export class FormComponent implements OnInit, OnChanges {

    @Input() fields: QuestionBase<any>[] = [];
    @Input() saveUrl: string;
    @Input() cancelRoute: string;
    @Input() data: any;
    @Input() delegateSave: boolean;
    @Input() clearOnSave: boolean;
    @Input() saveButton: any;
    @Input() labelCol: number = 3;
    @Input() progress: boolean;
    @Input() saveDisabled: boolean = false;
    @Input() refresh: Observable<any>;
    @Input() refreshForm: Observable<any>;
    @Input() config: any;
    @Output() saveFinished: EventEmitter<any> = new EventEmitter();
    @Output() change: EventEmitter<any> = new EventEmitter();
    @Output() unsavedChanges = new Subject();
    @Output() actionClicked = new Subject();
    @Output() onChanges = new Subject();

    form: FormGroup;
    payLoad = '';
    questions: QuestionBase<any>[];
    isInProgress: boolean = false;

    private questionsSub = {};

    private sub: Subscription;

    constructor(
        private qcs: QuestionControlService,
        private formService: FormService,
        private notificationService: NotificationService,
        private utilService: UtilService,
        private router: Router
    ) { }

    ngOnInit() {
        if (!this.saveUrl && !this.delegateSave) {
            throw new Error('ITPx Form component cannot work without [saveUrl]');
        }
        if (!this.fields) {
            throw new Error('ITPx Form component cannot work without [fields]');
        }
        if (!this.delegateSave && this.progress !== undefined) {
            throw new Error('ITPx Form with no [delegateSave] cannot have [progress]');
        }

        this.saveButton = this.saveButton || {};
        this.saveButton.icon = this.saveButton.icon || 'fa-save';
        this.saveButton.label = this.saveButton.label || 'Save Changes';

        this.questions = this.buildQuestions();
        this.form = this.qcs.toFormGroup(this.questions);

        if (this.refreshForm)
            this.refreshForm.subscribe(data => {
                this.data = data;
                this.questions = this.buildQuestions();
            })

        }
    ngOnDestroy() {
        for (let field of this.fields) {
            if (this.questionsSub[field.key]) {
                this.questionsSub[field.key].unsubscribe();
            }
        }
    }

    private unsavedData() {
        this.unsavedChanges.next(true);
    }

    private onActionClicked(e: any) {
        this.actionClicked.next(e)
    }

    ngOnChanges(changes: { [propKey: string]: SimpleChange }) {
        if (changes['data'] && this.questions) {
            this.questions = this.buildQuestions();
            this.form = this.qcs.toFormGroup(this.questions);
        }
        if (this.refreshForm) {
            this.questions = this.buildQuestions();
        }
    }
    onCancel() {
        if(this.cancelRoute.includes('/organization-details')) {
            let tab = this.cancelRoute.includes('tab') ? this.cancelRoute.split('=').pop() : null
            this.router.navigate(['/organization/organization-details'], tab ? { queryParams: { tab: tab } } : {})
        } 
        else return this.router.navigate([this.cancelRoute])
    }

    buildQuestions(): QuestionBase<any>[] {
        let questions = [];
        let self = this;
        return questions = this.fields.map((field: any) => {

            let question;
            switch (field['type']) {
                case 'hidden':
                    question = new HiddenQuestion({
                        key: field['key'],
                        label: field['label'],
                        required: field['required']
                    });
                    break;
                case 'list':
                    question = new ListQuestion({
                        key: field['key'],
                        label: field['label'],
                        dictionaryUrl: field['dictionaryUrl']
                    });

                    self.formService.getDictionary(field['dictionaryUrl'])
                        .subscribe(
                            (data) => {
                                let result = [];
                                let arr = this.data[question.key];
                                if (arr) {
                                    arr.forEach(segmentID => {
                                        data.forEach(element => {
                                            if (element.hasOwnProperty('key') && segmentID === element['key']) {
                                                result.push(element.value)
                                            }
                                        })
                                    });
                                }
                                question.value = result;
                            }
                        );

                    break;
                case 'text':
                    question = new TextQuestion({
                        key: field['key'],
                        label: field['label'],
                        required: field['required'],
                        hasTooltip: field['hasTooltip'],
                        tooltipTranslateKey: field['tooltipTranslateKey'],
                        hasExtraLink: field['hasExtraLink'],
                        manageLink: field['manageLink'],
                        hasExtraAction: field['hasExtraAction'],
                        actionLabel: field['actionLabel']
                    });
                    break;
                case 'date-text':
                    question = new TextDateQuestion({
                        key: field['key'],
                        label: field['label'],
                        required: field['required']
                    });
                    break;
                case 'numberbox':
                    question = new NumberboxQuestion({
                        key: field['key'],
                        label: field['label'],
                        required: field['required'],
                        type: 'number'
                    });
                    break;
                case 'dropdown':
                    question = new DropdownQuestion({
                        key: field['key'],
                        label: field['label'],
                        required: field['required'],
                        getterKey: field['getterKey'],
                        getterValue: field['getterValue'],
                        disabled: field['disabled'],
                        config: field['config']
                    });
                    if (this.questionsSub[question.key]) {
                        this.questionsSub[question.key].unsubscribe();
                    }
                    if (this.refresh) {
                        this.questionsSub[question.key] = this.refresh.subscribe(_ => self.formService.getDictionary(field['dictionaryUrl'])
                            .subscribe(
                                (data) => {
                                    question.options = data;
                                }));
                    }
                    self.formService.getDictionary(field['dictionaryUrl'])
                        .subscribe(
                            (data) => {
                                question.options = this.mapDictToArray(data);
                            }
                        );
                    break;
                case 'dynamic-dropdown':
                    question = new DynamicDropdownQuestion({
                        key: field['key'],
                        label: field['label'],
                        required: field['required'],
                        datasource: field['dictionaryUrl'],
                        refresh: this.refresh
                    });
                    break;
                case 'multi-dropdown':
                    question = new MultiDropdownQuestion({
                        key: field['key'],
                        label: field['label'],
                        required: field['required'],
                        config: field['config']
                    });
                    if (this.questionsSub[question.key]) {
                        this.questionsSub[question.key].unsubscribe();
                    }
                    if (this.refresh) {
                        this.questionsSub[question.key] = this.refresh.subscribe(_ => self.formService.getDictionary(field['dictionaryUrl'])
                            .subscribe(
                                (data) => {
                                    question.options = data;
                                }));
                    }
                    if (question.config && (question.config.dictType = 'extractDict')) {
                        self.formService.getDictionaryCombobox(field['dictionaryUrl'], question.config.filter)
                            .subscribe(
                                (data) => {
                                    question.options = this.extractDictionary(data, question.config.keyFieldName, question.config.valueFieldName)
                                }
                            );
                    } else {
                        self.formService.getDictionary(field['dictionaryUrl'])
                            .subscribe(
                                (data) => {
                                    question.options = this.mapDictToArray(data);
                                }
                            );
                    }

                    break;

                case 'combobox':
                    question = new ComboboxQuestion({
                        key: field['key'],
                        label: field['label'],
                        required: field['required'],
                        maxSelectedOptions: field['maxSelectedOptions'],
                        emitInputVal: field['emitInputVal'],
                        filterAmberDto: field['filterAmberDto']
                    });

                    self.formService.getDictionaryCombobox(field['dictionaryUrl'], field['filterAmberDto'])
                        .subscribe(
                            (data) => {
                                question.options = this.mapDictToArray(data);
                            }
                        );
                    break;

                case 'combobox-single-select':
                    question = new ComboboxSingleSelectQuestion({
                        key: field['key'],
                        label: field['label'],
                        required: field['required'],
                        maxShownResults: field['maxShownResults'],
                        emitInputVal: field['emitInputVal'],
                        placeholder: field['placeholder'],
                        filterAmberDto: field['filterAmberDto']
                    });
                    if (field.config && field.config.listDelegate) {
                        self.formService.delegateList(field['filterAmberDto'], field.config.listDelegate)
                            .subscribe(
                                (data) => {
                                    question.options = this.mapDictToArray(data);
                                });
                    }
                    else if (field.config && field.config.hardCodedOptions) {
                        question.options = field.config.hardCodedOptions
                    }
                    else {
                        self.formService.getDictionaryCombobox(field['dictionaryUrl'], field['filterAmberDto'])
                            .subscribe(
                                (data) => {
                                    question.options = this.mapDictToArray(data);
                                });
                    }
                    break;

                case 'datepicker':
                    question = new DatepickerQuestion({
                        key: field['key'],
                        label: field['label'],
                        required: field['required']
                    });
                    break;
                case 'datetimepicker':
                    question = new DatetimepickerQuestion({
                        key: field['key'],
                        label: field['label'],
                        required: field['required']
                    });
                    break;
                case 'radio':
                    question = new RadioQuestion({
                        key: field['key'],
                        label: field['label'],
                        required: field['required'],
                        options: field['options'],
                        default: field['default']
                    });
                    if (question.value == undefined) {
                        question.value = question['default'];
                    }
                    break;
                case 'textbox':
                    question = new TextboxQuestion({
                        key: field['key'],
                        label: field['label'],
                        type: field['inputType'],
                        required: field['required'],
                        placeholder: field['placeholder'],
                        maxlength: field['maxlength'],
                        pattern: field['pattern'],
                        trim: field['trim'],
                        uppercase: field['uppercase'],
                        disabled: field['disabled'],
                        validator: field['validator']
                    });
                    if (question.pattern == 'email') {
                        question.pattern = '^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,4}$'
                    } else if (question.pattern == 'gst') {
                        question.pattern = '^([0]{1}[1-9]{1}|[1-2]{1}[0-9]{1}|[3]{1}[0-7]{1})([a-zA-Z]{5}[0-9]{4}[a-zA-Z]{1}[1-9a-zA-Z]{1}[zZ]{1}[0-9a-zA-Z]{1})+$'
                    } else if (question.pattern == 'pan') {
                        question.pattern = '[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}';
                    }else if (question.pattern == 'url') {
                        question.pattern = new RegExp(UtilService.PATTERN_URL, 'i');
                    }
                    break;
                case 'textarea':
                    question = new TextareaQuestion({
                        key: field['key'],
                        label: field['label'],
                        required: field['required'],
                        pattern: field['pattern']
                    });
                    break;
                case 'multi-edit':
                    question = new MultiEditQuestion({
                        key: field['key'],
                        label: field['label'],
                        required: field['required'],
                        regex: field['regex']
                    });
                    break;
                case 'autocomplete':
                    question = new AutocompleteQuestion({
                        key: field['key'],
                        label: field['label'],
                        required: field['required']
                    });
                    question.disableCreateNewItem = field.hasOwnProperty('disableCreateNewItem') ? !!field['disableCreateNewItem'] : false;
                    question.allowSingleSelection = field.hasOwnProperty('allowSingleSelection') ? !!field['allowSingleSelection'] : false;
                    question.description = field['description'] || null;
                    
                    question.source = field['source'];
                    break;
                case 'checkbox':
                    question = new CheckboxQuestion({
                        key: field['key'],
                        label: field['label'],
                        required: field['required'],
                        text: field['text'],
                        value: field['value']
                    });
                    break;
                case 'html-editor':
                    question = new HtmlEditorQuestion({
                        key: field['key'],
                        label: field['label'],
                        required: field['required'],
                        modules: field['modules'],
                        placeholder: field['placeholder'],
                        style: field['style']
                    });
                    break;
                case 'img-upload':
                    question = new ImgUploadQuestion({
                        key: field['key'],
                        label: field['label'],
                        required: field['required'],
                        modules: field['modules'],
                        placeholder: field['placeholder'],
                        style: field['style']
                    });
                    break;
                default:
                    throw new TypeError('ITPx Form component does not support field type ['
                        + field['type'] + ']');
            }
            let val = (self.data && this.utilService.flatten(self.data)[field['key']]);
            if (val || val === 0) {
                question.value = val;
            }
            question.labelCol = self.labelCol;
            return question;
        });
    }

    onSubmit() {
        let result = this.utilService.unflatten(this.form.value);
        if (!this.delegateSave) {
            this.startProgress();
            this.formService.save(result, this.saveUrl)
                .subscribe(
                    data => {
                        this.endProgress();
                        this.notificationService.success('Successfully saved the data', 'Success');
                        this.saveFinished.emit(data);
                    },
                    error => this.endProgress()
                );
        } else {
            this.saveFinished.emit(result);
            if (this.clearOnSave) this.form.reset();
        }
    }

    startProgress() {
        this.isInProgress = true;
    }

    endProgress() {
        this.isInProgress = false;
    }

    private mapDictToArray(dto: any) {
        let result;
        if (!Array.isArray(dto)) {
            result = [];
            for (let key in dto) {
                if (dto.hasOwnProperty(key)) {
                    result.push({
                        key: key,
                        value: dto[key]
                    });
                }
            }
        } else {
            result = dto;
        }
        return result;
    }

    private extractDictionary(dto: any, keyFieldName: string, valueFieldName: string) {
        let result;
        if (Array.isArray(dto)) {
            result = [];
            dto.forEach(el => {
                if (el.hasOwnProperty(keyFieldName)) {
                    result.push({
                        key: el[keyFieldName],
                        value: el[valueFieldName]
                    })
                }
            })
        } else {
            result = dto;
        }
        return result;
    }
}



// WEBPACK FOOTER //
// ./src/app/shared/components/form/form.component.ts